import { Component, OnInit } from "@angular/core";
import { NotifierService } from "src/app/_services/notifier.service";
import { APIService } from "src/app/api/api.service";
import { environment } from "src/environments/environment";
import { AzureCostOptimizationBaseComponent } from "../azure-costoptimization-base.component";

@Component({
  selector: "app-azure-ri-recommendations",
  templateUrl: "./azure-ri-recommendations.component.html",
  styleUrls: [
    "../azure-costoptimization-base.component.css",
    "./azure-ri-recommendations.component.css",
  ],
})
export class AzureRiRecommendationsComponent
  extends AzureCostOptimizationBaseComponent
  implements OnInit
{
  pageHeading: string = "RI Recommendations";
  savingsCurrencyLabel: string = "INR";
  recommendationSummary: string =
    "Recommendations are based on Azure advisor reserved instance recommendations for the selected subscription.";
  protected scanAction: string = "ri-recommendations";

  headers: any[] = [
    { id: "resource_name", name: "Resource Name", filter: true },
    { id: "region", name: "Region", filter: true },
    { id: "term", name: "Term" },
    { id: "scope", name: "Scope" },
    { id: "recommended_quantity", name: "Recommended Qty" },
    {
      id: "estimated_monthly_savings",
      name: "Monthly Savings (INR)",
    },
    {
      id: "estimated_annual_savings",
      name: "Annual Savings (INR)",
    },
    { id: "impact", name: "Impact", filter: true },
    { id: "solution", name: "Solution", filter: true },
  ];

  totalRecommendationCount: number = 0;

  constructor(apiService: APIService, notifier: NotifierService) {
    super(apiService, notifier);
  }

  ngOnInit(): void {
    this.accountId = localStorage.getItem("accountId");
    this.resourceGroup = localStorage.getItem("selectedResourceGroup");
    this.selectedCloudPartner = localStorage.getItem("cp-selected");

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      let d = JSON.parse(msg);
      if (d.value == null) {
        return;
      }
      this.selectedCloudPartner = localStorage.getItem("cp-selected");
      if (d.key == "accountId") {
        this.accountId = d.value;
        this.refresh(true);
      } else if (d.key == "resourceGroup") {
        this.resourceGroup = d.value;
      }
    });

    this.refresh(true);
  }

  async refresh(showGlobalLoader: boolean = false) {
    if (showGlobalLoader) {
      this.notifier.loading(true);
    }

    if (this.selectedCloudPartner != "azure") {
      this.vmOptimizations = [];
      this.totalRecommendationCount = 0;
      this.loading = false;
      this.fetching = false;
      if (showGlobalLoader) {
        this.notifier.loading(false);
      }
      return;
    }

    this.pagination.page = 1;
    this.fetching = true;
    this.loading = true;

    try {
      const url = `${environment.apiURL}/${this.urlPrefix}/azure/advisor/api/advisor/ri-recommendations?${this.buildQuery(
        {
          user_id: this.userId,
          subscription_id: this.accountId,
        }
      )}`;

      let result: any = await this.apiService.getDataPromis(
        url,
        {},
        this.authHeader
      );

      if (this.destroyed) {
        return;
      }

       if (result && Array.isArray(result.items)) {
        this.totalRecommendationCount = Number(result.count || result.items.length || 0);
        this.vmOptimizations = result.items.map((item: any) =>
          this.normalizeRecommendation(item)
        );
        this.updateSavingsCurrencyLabel(this.vmOptimizations);
      } else {
        this.vmOptimizations = [];
        this.totalRecommendationCount = 0;
        this.updateSavingsCurrencyLabel([]);

        const err = this.parseRiError(result);
        if (err.toLowerCase().indexOf("no record") == -1) {
          this.notifier.alert("Error", "", err, "error", 5000);
        }
      }
    } catch (err: any) {
      this.vmOptimizations = [];
      this.totalRecommendationCount = 0;
      this.updateSavingsCurrencyLabel([]);
      this.notifier.alert("Error", "", err?.toString(), "error", 5000);
    } finally {
      this.loading = false;
      this.fetching = false;
      if (showGlobalLoader) {
        this.notifier.loading(false);
      }
    }
  }

  getTotalAnnualSavings() {
    const total = this.vmOptimizations.reduce((sum: number, item: any) => {
      return sum + Number(item.estimated_annual_savings || 0);
    }, 0);
    return total.toFixed(2);
  }

  getTotalSavings() {
    const total = this.vmOptimizations.reduce((sum: number, item: any) => {
      return sum + Number(item.estimated_monthly_savings || 0);
    }, 0);
    return total.toFixed(2);
  }

  private normalizeRecommendation(item: any): any {
    const extendedProperties = item.extended_properties || {};
    const monthlySavings =
      item.estimated_monthly_savings === 0 || item.estimated_monthly_savings
        ? item.estimated_monthly_savings
        : extendedProperties.savingsAmount;
    const annualSavings =
      item.estimated_annual_savings === 0 || item.estimated_annual_savings
        ? item.estimated_annual_savings
        : extendedProperties.annualSavingsAmount;

    return {
      ...item,
      currency: item.currency || extendedProperties.savingsCurrency || "INR",
      resource_name: item.resource_name || item.resource_id || "-",
      region: item.region || extendedProperties.region || "-",
      term: item.term || extendedProperties.term || "-",
      scope: item.scope || extendedProperties.scope || "-",
      recommended_quantity:
        item.recommended_quantity === 0 || item.recommended_quantity
          ? item.recommended_quantity
          : extendedProperties.displayQty || extendedProperties.qty || 0,
      estimated_monthly_savings:
        monthlySavings === 0 || monthlySavings ? Number(monthlySavings) : 0,
      estimated_annual_savings:
        annualSavings === 0 || annualSavings ? Number(annualSavings) : 0,
      impact: item.impact || "-",
      solution: item.solution || item.problem || "-",
    };
  }

  private updateSavingsCurrencyLabel(items: any[]) {
    const currency =
      items.find((item: any) => item?.currency)?.currency || "INR";
    this.savingsCurrencyLabel = currency;
    this.headers = this.headers.map((header: any) => {
      if (header.id == "estimated_monthly_savings") {
        return {
          ...header,
          name: `Monthly Savings (${currency})`,
        };
      }
      if (header.id == "estimated_annual_savings") {
        return {
          ...header,
          name: `Annual Savings (${currency})`,
        };
      }
      return header;
    });
  }

  private parseRiError(result: any): string {
    if (result && typeof result == "object" && Array.isArray(result.detail)) {
      return result.detail
        .map((item: any) => item?.msg || item?.type || "Validation error")
        .join(", ");
    }
    return this.parseApiError(result);
  }
}